import { DragEvent, useRef, useState } from "react";
import clsx from "clsx";
import { validateReportFile } from "../hooks/useReportUpload";
import "./ReportUpload.scss";

const formatSize = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

/** Pick or drop an aging report, check it, then upload it */
export default function ReportUpload({ busy, onUpload }: { busy: boolean; onUpload: (file: File) => Promise<boolean> }) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File>();
  const [error, setError] = useState<string>();
  const [dragging, setDragging] = useState(false);

  const choose = (candidate?: File) => {
    if (!candidate) return;
    const problem = validateReportFile(candidate);
    setError(problem);
    setFile(problem ? undefined : candidate);
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (!busy) choose(event.dataTransfer.files[0]);
  };

  const submit = async () => {
    if (file && (await onUpload(file))) setFile(undefined);
  };

  return (
    <div
      className={clsx("report-upload", { "is-dragging": dragging })}
      onDragOver={(event) => {
        event.preventDefault();
        if (!busy) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <input
        ref={input}
        className="report-upload-input"
        type="file"
        accept=".xlsx"
        aria-label="Choose report file"
        tabIndex={-1}
        onChange={(event) => {
          choose(event.target.files?.[0]);
          // Choosing the same file again after removing it must still register
          event.target.value = "";
        }}
      />

      {file ? (
        <div className="report-upload-file">
          <i className="fa-solid fa-file-excel" aria-hidden="true" />
          <span className="report-upload-name" title={file.name}>
            {file.name}
          </span>
          <small>{formatSize(file.size)}</small>
          <button type="button" className="report-upload-remove" aria-label="Remove file" onClick={() => setFile(undefined)} disabled={busy}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
          <button type="button" className="button is-success" onClick={submit} disabled={busy}>
            Upload report
          </button>
        </div>
      ) : (
        <button type="button" className="button report-upload-choose" onClick={() => input.current?.click()} disabled={busy}>
          <i className="fa-solid fa-upload" aria-hidden="true" />
          <span>Choose or drop a report</span>
        </button>
      )}

      {error && (
        <p className="report-upload-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
